import React, { createContext, useContext, useEffect, useState } from "react";
import { gcsPublicConfigured, gcsReadPublic } from "../storage";
import { captureShareKey, shareGranted } from "./shareLink";

/**
 * Site-wide switches, edited in /admin → Settings and stored in the public
 * bucket so they apply on the next page load, without a redeploy.
 *
 *   openToWork  true  - selling mode: Services page and nav link, "open to
 *                       selected engagements", work-with-me CTAs, availability
 *               false - showcase mode: all of that is hidden and the contact
 *                       blocks become a neutral "say hello"
 *   pages       one switch per page (impact, ventures, journal, about) and
 *               for the Contact button in the nav; false hides it from the
 *               nav (and the homepage preview), and the page's URL lands on
 *               the homepage. The contact page itself stays reachable.
 *   share       null, or { hash, expires } for the private share link that
 *               keeps hidden pages readable (see shareLink.js)
 */
export const SITE_SETTINGS_PATH = "production/config/site.json";
export const PAGE_KEYS = ["impact", "ventures", "journal", "about", "contact"];
export const DEFAULT_SETTINGS = {
	openToWork: true,
	pages: Object.fromEntries(PAGE_KEYS.map((key) => [key, true])),
	share: null,
};

/**
 * `raw` (a parsed site.json, possibly partial or from an older version) over
 * the defaults, page switches merged one by one so a missing key stays on.
 */
export function mergeSettings(raw) {
	const value = raw || {};
	return {
		...DEFAULT_SETTINGS,
		...value,
		pages: { ...DEFAULT_SETTINGS.pages, ...(value.pages || {}) },
	};
}

const CACHE_KEY = "pp-site-settings";

// The last value seen, so returning visitors don't watch selling copy flash
// in and disappear while the file loads.
function cached() {
	try {
		const raw = localStorage.getItem(CACHE_KEY);
		return mergeSettings(raw ? JSON.parse(raw) : null);
	} catch {
		return DEFAULT_SETTINGS;
	}
}

const SiteSettingsContext = createContext({ ...DEFAULT_SETTINGS, loaded: false, shared: false });

/**
 * Provides the site settings to the tree: the cached value at once, then the
 * bucket's site.json once loaded (`loaded` turns true either way; a failed
 * read keeps the cached/default value). `shared` is true when the visitor's
 * share key opens the current share link; it is settled before `loaded`.
 */
export function SiteSettingsProvider({ children }) {
	// Captured during the first render, before analytics sees the URL.
	const [shareKey] = useState(captureShareKey);
	const [settings, setSettings] = useState(() => ({ ...cached(), loaded: false, shared: false }));

	useEffect(() => {
		if (!gcsPublicConfigured()) {
			setSettings((s) => ({ ...s, loaded: true }));
			return;
		}
		gcsReadPublic(SITE_SETTINGS_PATH)
			.then(async (raw) => {
				const next = mergeSettings(raw ? JSON.parse(raw) : null);
				try {
					localStorage.setItem(CACHE_KEY, JSON.stringify(next));
				} catch {}
				const shared = await shareGranted(next.share, shareKey);
				setSettings({ ...next, loaded: true, shared });
			})
			.catch(() => setSettings((s) => ({ ...s, loaded: true })));
	}, [shareKey]);

	return <SiteSettingsContext.Provider value={settings}>{children}</SiteSettingsContext.Provider>;
}

/** The current site settings: { openToWork, pages, share, loaded, shared }. */
export const useSiteSettings = () => useContext(SiteSettingsContext);
